import useCheckConsent from "@/hooks/consent/useCheckConsent";
import useGetEventBus from "@/hooks/useGetEventBus";
import routes from "@/router/routes";
import authStore, { resetAuthStore } from "@/stores/authStore";
import DidomiReact, { DidomiSDK } from "@didomi/react";
import { useSelector } from "@tanstack/react-store";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const DidomiConsent = () => {
    const navigate = useNavigate();
    const userEmail = useSelector(authStore, (state) => state.userEmail);
    const [consentManagerIsInitialized, setConsentManagerIsInitialized] =
        useState(false);
    const [didomiIsReady, setDidomiIsReady] = useState(false);

    const { mutateAsync: checkConsent } = useCheckConsent();

    const handleUserLoggedOut = () => {
        resetAuthStore();
        setDidomiIsReady(false);
        setConsentManagerIsInitialized(false);
    };

    const { off: offServicesCleanup } = useGetEventBus<void>(
        "services-cleanup",
        handleUserLoggedOut,
    );

    const handleStartupConsentManagerReady = async () => {
        setConsentManagerIsInitialized(true);
    };

    const { off: offUserAuthenticated } = useGetEventBus(
        "startup-consent-manager-ready",
        handleStartupConsentManagerReady,
    );

    useEffect(
        () => () => {
            offUserAuthenticated(
                "startup-consent-manager-ready",
                handleStartupConsentManagerReady,
            );
            offServicesCleanup("services-cleanup", handleUserLoggedOut);
        },
        [],
    );

    const checkAndSetConsent = async () => {
        const didomiConfig = (window as any).Didomi.getConfig();
        if (didomiConfig.user.organizationUserId !== userEmail?.toLowerCase()) {
            (window as any).Didomi.reset();
            (window as any).Didomi.setUser({
                organizationUserId: userEmail?.toLowerCase(),
            });
        }

        const didomiShouldCollect = (
            window as any
        ).Didomi.shouldUserStatusBeCollected();
        const didomiUserStatus = (window as any).Didomi.getCurrentUserStatus();

        const mailVistaConsentIsOk = await checkConsent({
            didomiUserProfile: didomiUserStatus,
            didomiShouldCollect,
        });

        authStore.setState((prevState) => ({
            ...prevState,
            userConsentsAreAccepted: mailVistaConsentIsOk,
        }));
    };

    useEffect(() => {
        if (didomiIsReady && consentManagerIsInitialized) {
            checkAndSetConsent();
        }
    }, [didomiIsReady, consentManagerIsInitialized]);

    const onDidomiReady = async (didomi: DidomiReact.IDidomiObject) => {
        didomi.preferences.hide();
        setDidomiIsReady(true);
    };

    const onDidomiPreferencesClickSaveChoices = async () => {
        const didomiInstance = (window as any).Didomi;

        if (!didomiInstance) {
            console.error("Didomi instance is not available");

            return;
        }

        const updatedUserStatus = didomiInstance.getCurrentUserStatus();
        const updatedDidomiShouldCollect =
            didomiInstance.shouldUserStatusBeCollected();

        const mailVistaConsentIsOk = await checkConsent({
            didomiUserProfile: updatedUserStatus,
            didomiShouldCollect: updatedDidomiShouldCollect,
        });

        authStore.setState((prevState) => ({
            ...prevState,
            userConsentsAreAccepted: mailVistaConsentIsOk,
            hasBeenRedirectedToConsent: true,
        }));

        if (mailVistaConsentIsOk) {
            navigate(routes.dashboard);
        }
    };

    if (!userEmail) {
        return null;
    }

    return (
        <DidomiSDK
            key={`didomi-${userEmail}`}
            apiKey={import.meta.env.VITE_APP_DIDOMI_API_KEY}
            config={{
                organizationId: import.meta.env.VITE_APP_DIDOMI_ORGANIZATION_ID,
                user: {
                    organizationUserId: userEmail.toLowerCase(),
                },
            }}
            noticeId={import.meta.env.VITE_APP_DIDOMI_NOTICE_ID}
            onPreferencesClickSaveChoices={onDidomiPreferencesClickSaveChoices}
            onReady={onDidomiReady}
            sdkPath={import.meta.env.VITE_APP_DIDOMI_SDK_PATH}
        />
    );
};

export default DidomiConsent;
